import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatDateShort, formatViews } from '@/utils/articles';
import { getCategoryMeta } from '@/mocks/categories';
import FavoriteButton from '@/components/feature/FavoriteButton';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'row';
}

export default function ArticleCard({ article, variant = 'grid' }: ArticleCardProps) {
  const category = getCategoryMeta(article.category);

  if (variant === 'row') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-28 sm:w-40 h-24 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 91吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
              {category?.name}
            </span>
            <span className="text-xs text-foreground-400 whitespace-nowrap">
              {formatDateShort(article.publishTime)}
            </span>
          </div>
          <h3 className="mt-2 font-heading font-bold text-base text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-sm text-foreground-500 line-clamp-2 hidden sm:block">
            {article.summary}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-4 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-share-forward-line" />
              {article.shares}
            </span>
            <span className="ml-auto">
              <FavoriteButton id={article.id} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full aspect-[16/10] overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 91吃瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col flex-1 p-4">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
            {category?.name}
          </span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">
            {formatDateShort(article.publishTime)}
          </span>
        </div>
        <h3 className="mt-2.5 font-heading font-bold text-base leading-snug text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{article.summary}</p>
        <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-400">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="inline-flex items-center gap-1 ml-auto">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <FavoriteButton id={article.id} />
        </div>
      </div>
    </Link>
  );
}